import React from 'react';

import { useParams } from 'react-router-dom';
import { filterScheduledExamSectionSubScores } from '@components/Component/Exams/hooks/helpers';
import { useGetExamDetail } from '@components/Component/Exams/hooks/query';
import { useCurrentSection } from '@reducers/sections/hooks';
import ReportTabs from './ReportTabs';
import { Testlet } from '../../types';
import { useGetTestlets } from '../../hooks';
import { TabGeneratorInterface, TabKey } from './types';

const TabGenerator: TabGeneratorInterface<any> = {
  generateTabs: <T extends TabKey>(data: T[] | undefined) =>
    data?.map((item) => ({ id: item.id, name: item.name })) || [],

  findDataByTabKey: <T extends TabKey>(data: T[] | undefined, tabKey: TabKey) =>
    data?.find((item) => item.id === tabKey.id),
};

interface TestletReportTabsProps {
  ReportComponent: React.ComponentType<{ data?: Testlet }>;
  excludeBranching?: boolean;
}

export const TestletReportTabs = ({ ReportComponent, excludeBranching }: TestletReportTabsProps) => {
  const { testSessionId } = useParams();
  const { data: testlets } = useGetTestlets(testSessionId);
  // Item-adaptive (branching) testlets have no meaningful per-question
  // matrix — callers that render such widgets exclude them entirely
  const visibleTestlets = excludeBranching
    ? testlets?.filter((testlet) => !testlet.is_branching)
    : testlets;

  return (
    <ReportTabs
      data={visibleTestlets}
      tabGenerator={TabGenerator}
      reportsComponent={ReportComponent}
    />
  );
};

export const ScoreSubjectReportTabs = ({ ReportComponent }: TestletReportTabsProps) => {
  const { data: examDetails } = useGetExamDetail();
  const scheduledExamSectionsSubScores = filterScheduledExamSectionSubScores(
    examDetails,
    examDetails?.exam.scores.map((score) => ({ id: score.score_id, ...score })),
  );

  if (!examDetails) {
    return null;
  }

  return (
    <ReportTabs
      data={
        scheduledExamSectionsSubScores.map((score) => ({
          id: score.score_id,
          name: score.name,
        })) || []
      }
      tabGenerator={TabGenerator}
      reportsComponent={ReportComponent}
    />
  );
};
/** Subjects whose reports are relevant for this section: lesson-scheduled
 * subjects with packages, falling back to all packaged subjects for sections
 * that only schedule non-exam subjects (e.g. Clay PM sections schedule only
 * "Introduction" while testing Algebra 1 / Algebra 1A). */
export const getReportSubjects = (section: ReturnType<typeof useCurrentSection>) => {
  const packaged = section?.subjects?.filter((subject) => subject.packages?.length > 0) ?? [];
  const scheduled = packaged.filter(
    (subject) => section?.section_scheduled_subjects[subject.id],
  );
  return scheduled.length > 0 ? scheduled : packaged;
};

export const CurriculumSubjectReportTabs = ({
  ReportComponent,
}: TestletReportTabsProps) => {
  const section = useCurrentSection();
  if (!section) {
    return null;
  }
  const mappedData = getReportSubjects(section).map((subject) => ({
    id: subject.id,
    name: subject.display_name,
  }));

  return (
    <ReportTabs
      data={mappedData}
      tabGenerator={TabGenerator}
      reportsComponent={ReportComponent}
    />
  );
};
